import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Card from "components/ui/card/Card";
import EmptyState from "components/ui/empty_state/EmptyState";
import Tooltip from "components/ui/tooltip/Tooltip";
import { SavedSuppliers, useSavedSuppliers } from "lib/hooks/api/signals/useSavedSuppliers";
import { useSubscription } from "lib/hooks/subscription";
import { useProHelper } from "lib/providers/ProHelper";
import { useTracking } from "lib/tracking";
import { SPEND_PRESETS, SpendPreset } from "./config";
import { createViewSelectedEvent } from "./tracking";

type Props = {
  /** Applies the chosen template; the paywall check and tracking have already happened. */
  onSelect: (preset: SpendPreset) => void;
};

/** Why a template can't be used right now, e.g. no competitors saved. Undefined means it can. */
function disabledReasonFor(
  preset: SpendPreset,
  savedSuppliers: SavedSuppliers | undefined,
): string | undefined {
  if (savedSuppliers === undefined) {
    // Still loading; the token can be stored safely while it resolves.
    return undefined;
  }
  if (
    savedSuppliers === null ||
    (preset.requires === "competitors" && savedSuppliers.competitorGuids.length === 0) ||
    (preset.requires === "partners" && savedSuppliers.partnerGuids.length === 0)
  ) {
    return `Save some suppliers as ${preset.requires} first, then this template can show them.`;
  }
  return undefined;
}

/**
 * What the page shows before any filter is applied, and again after Clear all. Distinct
 * from the no-results state: nothing has been searched yet.
 */
export function SpendStartingState({ onSelect }: Props) {
  const { data: savedSuppliers } = useSavedSuppliers();
  const subscription = useSubscription();
  const { checkSubscription } = useProHelper();
  const { logEvents } = useTracking();

  const select = (preset: SpendPreset) => {
    if (!subscription.hasDataTypes("SUPPLIERS")) {
      checkSubscription("SUPPLIERS");
      return;
    }
    logEvents(createViewSelectedEvent(preset.title));
    onSelect(preset);
  };

  return (
    <Container aria-label="Spend starting state">
      <EmptyState
        size="large"
        type="startingState"
        width="100%"
        heading="Start with a template, or pick your filters"
        description="Templates look at your saved competitors or partners over the last three years. Or choose buyers, suppliers and dates in the panel on the left."
      />
      <PresetGrid>
        {SPEND_PRESETS.map((preset) => {
          const disabledReason = disabledReasonFor(preset, savedSuppliers);
          const card = (
            <Card
              key={preset.title}
              fullWidth
              disabled={disabledReason !== undefined}
              onClick={() => select(preset)}
              aria-label={preset.title}
            >
              <Flex column gap={4}>
                <Text fontWeight="600">{preset.title}</Text>
                <Text description color="sysTextSecondary">
                  {preset.description}
                </Text>
              </Flex>
            </Card>
          );
          if (!disabledReason) {
            return card;
          }
          return (
            <Tooltip key={preset.title} content={disabledReason} side="bottom">
              <div>{card}</div>
            </Tooltip>
          );
        })}
      </PresetGrid>
    </Container>
  );
}

const Container = styled.section({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 32,
  padding: "48px 24px",
  maxWidth: 960,
  margin: "0 auto",
  width: "100%",
});

const PresetGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  gap: 16,
  width: "100%",
});
